import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useJoinWaitlist } from "../../../lib/api/waitlist.api";
import { track } from "../../../lib/analytics";
import { Logo } from "../../../ui/icons";

interface LandingNavProps {
  readonly isDark: boolean;
  readonly onToggleTheme: () => void;
}

const NAV_LINKS = [
  { label: "Nouveautés", path: "/releases" },
  { label: "Roadmap", path: "/roadmap" },
];

export function LandingNav({ isDark, onToggleTheme }: LandingNavProps) {
  const [location, navigate] = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState<false | "new" | "already">(false);
  const joinWaitlist = useJoinWaitlist();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    joinWaitlist.mutate(
      { email: email.trim() },
      {
        onSuccess: (data) => {
          const isNew = data.status === "success";
          setSubscribed(isNew ? "new" : "already");
          if (isNew) track("landing_nav_waitlist_submitted", {});
          setEmail("");
        },
      },
    );
  };

  const handleNavigate = (path: string) => {
    track("landing_nav_clicked", { link: path });
    navigate(path);
    setMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? "bg-surface/80 backdrop-blur-xl shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <button
            onClick={() => handleNavigate("/")}
            className="flex items-center gap-2 text-lg font-bold text-ink tracking-tight"
          >
            <Logo className="size-6" />
            Palnia
          </button>

          <div className="hidden sm:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.path}
                onClick={() => { track("landing_nav_clicked", { link: link.path }); navigate(link.path); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  location === link.path
                    ? "text-ink bg-surface-alt"
                    : "text-ink-muted hover:text-ink hover:bg-surface-alt/50"
                }`}
              >
                {link.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Waitlist — desktop */}
          {!subscribed ? (
            <form
              onSubmit={handleSubmit}
              className="hidden lg:flex items-center gap-1.5 mr-2"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Rejoindre la waitlist"
                required
                className="w-40 px-2.5 py-1.5 text-xs bg-surface-alt/50 border border-border
                  text-ink placeholder:text-ink-faint rounded-lg focus:outline-none
                  focus:ring-1 focus:ring-accent/30"
              />
              <button
                type="submit"
                disabled={joinWaitlist.isPending}
                className="px-2.5 py-1.5 text-xs font-medium text-ink-muted border border-border
                  hover:text-ink hover:border-border-strong rounded-lg transition-colors
                  whitespace-nowrap"
              >
                OK
              </button>
            </form>
          ) : (
            <span className={`hidden lg:block text-xs mr-2 ${subscribed === "already" ? "text-accent" : "text-success"}`}>
              {subscribed === "already" ? "Déjà inscrit !" : "Inscrit !"}
            </span>
          )}

          <button
            onClick={() => { track("landing_theme_toggled", { theme: isDark ? "light" : "dark" }); onToggleTheme(); }}
            className="w-9 h-9 flex items-center justify-center rounded-xl
              text-ink-muted hover:text-ink hover:bg-surface-alt transition-colors"
            aria-label="Changer le thème"
          >
            {isDark ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
              </svg>
            )}
          </button>

          <button
            onClick={() => { track("landing_cta_clicked", { cta: "register" }); navigate("/register"); }}
            className="hidden sm:block px-4 py-2 text-sm font-medium text-white bg-accent hover:bg-accent-hover rounded-xl transition-colors"
          >
            Essai gratuit
          </button>

          {/* Burger — mobile */}
          <button
            onClick={() => { const next = !menuOpen; track("landing_mobile_menu_toggled", { opened: next }); setMenuOpen(next); }}
            className="sm:hidden w-9 h-9 flex items-center justify-center rounded-xl
              text-ink-muted hover:text-ink hover:bg-surface-alt transition-colors"
            aria-label="Menu"
          >
            {menuOpen ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Menu mobile */}
      {menuOpen && (
        <div className="sm:hidden bg-surface/95 backdrop-blur-xl">
          <div className="flex flex-col px-6 py-3 gap-0.5">
            <div className="flex gap-2">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.path}
                  onClick={() => handleNavigate(link.path)}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                    location === link.path
                      ? "text-ink font-medium bg-surface-alt"
                      : "text-ink-muted"
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </div>
            <div className="border-t border-border/50 my-1.5" />
            <div className="flex gap-2">
              <button
                onClick={() => handleNavigate("/login")}
                className="flex-1 py-2 text-sm text-ink-muted border border-border rounded-lg transition-colors text-center"
              >
                Connexion
              </button>
              <button
                onClick={() => handleNavigate("/register")}
                className="flex-1 py-2 text-sm font-medium text-white bg-accent rounded-lg transition-colors text-center"
              >
                Essai gratuit
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
